// Shared detection of no-go-zone entry/exit waypoints on a route's geometry.
// A waypoint's `properties.zones` lists every zone the waypoint relates to:{ id, type, name, transition }
// Only zones of type "bpr_nogo" (precautionary zones) are relevant here; other
// types (eca, whale, ...) are ignored. The `transition` attribute tells what the
// waypoint is for that zone: "entry", "exit" or "inside" (only entry/exit are
// crossing points). The label shown in the UI is the zone `name` plus its
// transition, e.g. "Strait Of Gibraltar - Entry".
//
// Used both by the map layer (to style the markers) and by the route editor's
// warnings review (to list and label the entry/exit points), so the two never
// diverge.

export type ZonePointType = "entry" | "exit";

export const NO_GO_ZONE_TYPE = "bpr_nogo";

export interface ZonePoint {
  type: ZonePointType;
  // Stable key of the point: "lon,lat|zoneId". The coordinate part matches the
  // OL point before projection, the zone id keeps two zones that share a
  // waypoint apart.
  key: string;
  coordinates: [number, number];
  zoneId: string;
  zoneName: string;
  // "<zone name> - Entry" / "<zone name> - Exit", ready to show in the UI.
  label: string;
  properties: Record<string, any>;
}

export interface ZoneCrossingItem {
  point: ZonePoint;
  zoneNumber: number;
}

// Coordinate key of a waypoint, matching the raw lon/lat coordinates (the map
// layer reads the same coordinates before applying the map projection).
export function wpCoordKey(coordinates: number[]): string {
  return `${coordinates[0]},${coordinates[1]}`;
}

// Full key of a zone point: coordinate key + the zone it belongs to.
export function zonePointKey(coordinates: number[], zoneId: string): string {
  return `${wpCoordKey(coordinates)}|${zoneId}`;
}

function normalize(value: any): string {
  return String(value ?? "").trim().toLowerCase();
}

function isNoGoZone(zone: any): boolean {
  return normalize(zone?.type) === NO_GO_ZONE_TYPE;
}

function transitionType(zone: any): ZonePointType | null {
  const transition = normalize(zone?.transition);
  return transition === "entry" || transition === "exit" ? transition : null;
}

export function getEntryExitPoints(routeGeometry: any[]): ZonePoint[] {
  const result: ZonePoint[] = [];
  const waypoints = (routeGeometry || []).filter(
    (f: any) => f?.geometry?.type === "Point"
  );

  for (const wp of waypoints) {
    const coordinates = wp?.geometry?.coordinates;
    if (!coordinates) continue;

    const zones = wp?.properties?.zones;
    if (!Array.isArray(zones)) continue;

    for (const zone of zones) {
      if (!isNoGoZone(zone)) continue;

      const type = transitionType(zone);
      if (!type) continue;

      const zoneId = String(zone?.id ?? "");
      const zoneName = String(zone?.name ?? "").trim();
      result.push({
        type,
        key: zonePointKey(coordinates, zoneId),
        coordinates: [coordinates[0], coordinates[1]],
        zoneId,
        zoneName,
        label: zoneLabel(zoneName, type),
        properties: wp.properties || {},
      });
    }
  }
  return result;
}

// "Strait Of Gibraltar - Entry" (falls back to "No-Go Zone" when unnamed).
export function zoneLabel(zoneName: string, type: ZonePointType): string {
  const transition = type === "entry" ? "Entry" : "Exit";
  return `${zoneName || "No-Go Zone"} - ${transition}`;
}

// Numbers the crossings of a route: each entry opens a crossing of that zone,
// the matching exit (same zone id) closes it. An exit without a preceding entry
// (route starting inside a zone) counts as a crossing of its own. Shared by the
// route editor's review and the crossing count so both stay in sync.
export function getZoneCrossingItems(points: ZonePoint[]): ZoneCrossingItem[] {
  let zoneNumber = 0;
  const openZones = new Map<string, number>();

  return (points || []).map((point) => {
    let zone: number;
    if (point.type === "entry") {
      zoneNumber += 1;
      zone = zoneNumber;
      openZones.set(point.zoneId, zoneNumber);
    } else {
      const open = openZones.get(point.zoneId);
      if (open !== undefined) {
        zone = open;
        openZones.delete(point.zoneId);
      } else {
        zoneNumber += 1;
        zone = zoneNumber;
      }
    }
    return { point, zoneNumber: zone };
  });
}

// Number of no-go-zone crossings on a route. Matches the zone numbering in the
// route editor's review.
export function getCrossingZoneCount(routeGeometry: any[]): number {
  return getZoneCrossingItems(getEntryExitPoints(routeGeometry)).reduce(
    (max, item) => Math.max(max, item.zoneNumber),
    0
  );
}

// A route needs its route warnings reviewed while it crosses precautionary zones and the user has
// not acknowledged them in the route editor yet. Pure data check - callers gate it on
// const_pow_endpoint_v2 themselves, so this stays free of the feature flag plumbing.
export function routeNeedsWarningsReview(route: any): boolean {
  if (!route || getEntryExitPoints(route.route_geometry || []).length === 0) {
    return false;
  }
  return route.route_info?.routeWarningsReviewed !== true;
}

// The mirror image of routeNeedsWarningsReview: the route crosses precautionary zones and the user
// has already acknowledged them, so the UI reports the review as done instead of pending. Kept
// separate from "not needing a review" because a route with no crossings is neither.
export function routeWarningsReviewed(route: any): boolean {
  if (!route || getEntryExitPoints(route.route_geometry || []).length === 0) {
    return false;
  }
  return route.route_info?.routeWarningsReviewed === true;
}

// Identity of a route's zone crossings: every entry/exit point in order, with the zone it belongs
// to, its transition and its position. Two routes sharing a signature cross exactly the same zones
// at exactly the same waypoints, which is what lets a "reviewed" acknowledgement survive a
// recalculation. Any change - a zone gained or lost, or a crossing point moved - yields a different
// signature, so the review is required again.
export function getZoneCrossingSignature(routeGeometry: any[]): string {
  // Trim the last 2 of the ~14 decimals so sub-millimetre optimiser drift doesn't invalidate a review.
  const roundCoord = (n: number) => Number(n.toFixed(12));
  return getEntryExitPoints(routeGeometry)
    .map((point) => `${point.zoneId}:${point.type}:${roundCoord(point.coordinates[0])},${roundCoord(point.coordinates[1])}`)
    .join("|");
}

// Degrees + decimal minutes, e.g. "12°45.23'N, 58°12.45'E".
export function formatZoneCoordinate(coordinates: [number, number]): string {
  const [lon, lat] = coordinates;
  return `${degreesDecimalMinutes(lat, "N", "S")}, ${degreesDecimalMinutes(lon, "E", "W")}`;
}

function degreesDecimalMinutes(value: number, pos: string, neg: string): string {
  const hemisphere = value >= 0 ? pos : neg;
  const abs = Math.abs(value);
  const degrees = Math.floor(abs);
  const minutes = ((abs - degrees) * 60).toFixed(2);
  return `${degrees}°${minutes}'${hemisphere}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "27 Aug 2026, 19:50 UTC", or an em dash when the waypoint has no calculated time yet.
export function formatZoneTime(value: any): string {
  if (!value || typeof value !== "string") return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const day = date.getUTCDate();
  const month = MONTHS[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hours}:${minutes} UTC`;
}

// Text for the "?" hint next to a zone waypoint: the zone's own description when the data carries
// one, otherwise its name and transition.
export function getZoneDescription(point: ZonePoint): string {
  const properties = point.properties || {};
  return properties.description || properties.zone_description || point.label || properties.name
    || "No description available.";
}
